import { Callout, Classes, H5, Icon, Intent } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import classNames from 'classnames';
import * as React from 'react';
import { Link, Text } from 'vet-bones/components';

import {
  DataSourceType,
  UNKNOWN_DATA_SOURCE_TYPE,
} from 'src/constants/dataSourceTypeDescription';
import { TEST_IDS } from 'src/constants/testIds';
import { connectionSelector } from 'src/selectors/connection';
import { currentStardogVersionSelector } from 'src/selectors/currentStardogVersion';
import { dataSourceLabelsByTypeSelector } from 'src/selectors/dataSourceLabelsByType';
import { components } from 'src/templates/copy';
import { getDataSourceIcon, icons } from 'src/templates/icons';
import { colors } from 'src/templates/styles/colors';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';

type DataSourcePanelContainerProps = {
  children?: React.ReactNode;
  connectionId?: string;
  connectionEndpoint?: string;
  connectionType?: DataSourceType;
  handleUpdateDataSource?: () => void;
  warningMessage?: string;
};

export const DataSourcePanelContainer: React.VFC<
  DataSourcePanelContainerProps
> = ({
  children,
  connectionId,
  connectionEndpoint,
  connectionType = null,
  handleUpdateDataSource,
  warningMessage,
}) => {
  const connection = useReduxSelector(connectionSelector);

  let dataSourceInfo = null;
  if (connectionId && connectionEndpoint) {
    // TODO: Need a more general approach to showing this
    // information when non-JDBC data sources are supported
    dataSourceInfo = (
      <>
        <b
          data-testid={
            TEST_IDS.projectResourceDialog.dataSourcePanel.dataSourceInfo
          }
        >
          <Text ellipsize>{connectionId}</Text>
        </b>
        <Text ellipsize>
          {components.projectResourceDialog.projectDataSourcePanel.endpoint}:{' '}
          {connectionEndpoint}
        </Text>
      </>
    );
  } else if (connectionId) {
    dataSourceInfo = (
      <b
        data-testid={
          TEST_IDS.projectResourceDialog.dataSourcePanel.dataSourceInfo
        }
      >
        <Text ellipsize>{connectionId}</Text>
      </b>
    );
  }

  const dataSourceLabelsByType = useReduxSelector(
    dataSourceLabelsByTypeSelector(connection)
  );
  const stardogVersion = useReduxSelector(currentStardogVersionSelector);
  let typeInfo = null;
  if (connectionType) {
    const { isBlueprintIcon, icon } = getDataSourceIcon({
      dataSourceType: connectionType,
      stardogVersion,
    });
    const label =
      dataSourceLabelsByType[connectionType] ||
      dataSourceLabelsByType[UNKNOWN_DATA_SOURCE_TYPE];
    typeInfo = (
      <div className="sd-data-source-panel__type-info">
        {isBlueprintIcon ? (
          <Icon icon={icon} iconSize={24} />
        ) : (
          <img alt={`${label} icon`} height="24px" src={icon} />
        )}
        <div
          data-testid={TEST_IDS.projectResourceDialog.dataSourcePanel.typeInfo}
        >
          {label}
        </div>
      </div>
    );
  }

  if (!connection && !dataSourceInfo) {
    return null;
  }

  return (
    <Callout className="sd-data-source-panel" intent={Intent.NONE}>
      {warningMessage ? (
        <Callout
          className={classNames(
            Classes.INTENT_WARNING,
            'sd-data-source-panel__warning'
          )}
        >
          <Icon icon={IconNames.WARNING_SIGN} />
          {warningMessage}
        </Callout>
      ) : null}
      <div className="sd-data-source-panel__connection-info">
        <div className="sd-data-source-panel__connection-info__column">
          {connection?.name ? (
            <div className="sd-data-source-panel__connection-name">
              <Icon
                color={colors.darkIcon}
                icon={icons.connection}
                iconSize={24}
              />
              <H5
                data-testid={
                  TEST_IDS.projectResourceDialog.dataSourcePanel.connectionInfo
                }
              >
                {connection?.name}
              </H5>
            </div>
          ) : null}
          {typeInfo}
        </div>
        <div className="sd-data-source-panel__connection-info__column">
          {dataSourceInfo}
        </div>
        {handleUpdateDataSource ? (
          <div className="sd-data-source-panel__connection-info__update-column">
            <Link
              onClick={handleUpdateDataSource}
              testId={TEST_IDS.projectVirtualGraphPanel.replaceDataSource}
            >
              <Text ellipsize>
                {components.virtualGraphPreviewDialog.replaceDataSourceButton}
              </Text>
            </Link>
          </div>
        ) : null}
      </div>
      {children ? (
        <div className="sd-data-source-panel__body">{children}</div>
      ) : null}
    </Callout>
  );
};
